import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import * as Sentry from '@sentry/browser';

import SimcInput from 'shared/simcinput';
import { className as getClassName, activeLoadout, getCharacterSpecInfo, getFaction } from 'shared/character';
import { getSelectedGear, getCombinations } from 'shared/combinations';
import Droptimizer from 'shared/droptimizer';
import {
  getWeaponHands,
  isItemUsable,
  updateItemUpgradeLevel,
} from 'shared/items';
import {
  getGemDetailsByEnchantId,
} from 'shared/gems';
import { CURRENT_SEASON, SOCKET_TYPES } from 'shared/constants';
import { getEnchants } from 'shared/enchants';

import { findExpacOption } from 'shared/expansionOptions';

import jobs from './jobs';
import archivedJobs from './archivedJobs';
import realms from './realms';

import form, { selectors as formSelectors } from './form';
import gearsets, { selectors as gearsetsSelectors } from './gearsets';
import character, { selectors as characterSelectors } from './character';
import savedGearsets from './savedGearsets';
import selectedSavedGearset from './selectedSavedGearset';
import simcItems, { selectors as simcItemsSelectors } from './simcItems';
// need to figure out how to clean up expansion option references without cycles

import simcOptions, { selectors as simcOptionsSelectors, expansionOptionConfig } from './simcOptions';
import talentSets, { selectors as talentSetsSelectors } from './talentSets';
import optimize, { selectors as optimizeSelectors } from './optimize';
import droptimizer, { selectors as droptimizerSelectors } from './droptimizer';
import { selectors as gemEnchantSelectors } from './optimize/gemEnchant';
import { selectors as enhancementSelectors } from './optimize/enhancements';
import { selectors as talentTreeSelectors } from './optimize/talentTrees';
import customApls, { selectors as customAplsSelectors } from './customApls';
import report from './report';
import reportOptions from './reportOptions';
import simcExpertOptions, { selectors as simcExpertSelectors } from './simcExpertOptions';

export default combineReducers({
  archivedJobs,
  character,
  customApls,
  droptimizer,
  form,
  gearsets,
  jobs,
  optimize,
  realms,
  report,
  reportOptions,
  savedGearsets,
  selectedSavedGearset,
  simcExpertOptions,
  simcItems,
  simcOptions,
  talentSets,
});

const equippedGearSelector = createSelector(
  characterSelectors.data,
  simcItemsSelectors.items,
  (state, props) => props?.type,
  droptimizerSelectors.selectedUpgradeLevel,
  droptimizerSelectors.upgradeEquipped,
  (char, itemsFromSimc, simType, droptimizerUpgradeLevel, droptimizerUpgradeEquipped) => {
    let items = {};

    if (char && char.items) {
      items = _.pickBy(
        char.items,
        _.isObject,
      );
    } else if (itemsFromSimc) {
      items = itemsFromSimc;
    }

    let targetUpgradeBonus;

    if (simType === 'droptimizer' && droptimizerUpgradeLevel && droptimizerUpgradeEquipped) {
      targetUpgradeBonus = droptimizerUpgradeLevel;
    }

    if (targetUpgradeBonus) {
      items = _.mapValues(items, (item) => {
        const newItem = updateItemUpgradeLevel(item, targetUpgradeBonus);
        return newItem;
      });
    }

    return items;
  },
);

const characterSelector = createSelector(
  characterSelectors.data,
  formSelectors.text,
  optimizeSelectors.data,
  simcItemsSelectors.items, // ugh - include this so that an API call to simc-items will re-run this selector
  // the reason this needs to happen is because the simc-items API call will populate a global ItemCache
  // that is needed to get more item info into the character object when using SimcInput.toCharacter
  // It's nasty as hell but can be super useful to get detailed item data that comes from the server and is cached
  // in the client.
  (state, props) => _.get(props, 'type', null),
  (char, simcInput, optimizeData, items, simType) => {
    if (simType === 'optimize') {
      return SimcInput.toCharacter(optimizeData);
    }

    if (char) {
      return char;
    }

    try {
      return SimcInput.toCharacter(SimcInput.parse(simcInput));
    } catch (error) {
      Sentry.captureException(`Unable to convert input to character: ${error.message}`, {
        extra: {
          simcInput,
        },
      });
      throw error;
    }
  },
);


const characterSpecInfoSelector = createSelector(
  characterSelector,
  char => getCharacterSpecInfo(char),
);

const characterClassNameSelector = createSelector(
  characterSelector,
  char => getClassName(char) || '',
);

const characterClassIdSelector = createSelector(
  characterSpecInfoSelector,
  specInfo => _.get(specInfo, 'class.id'),
);

const characterSpecIdSelector = createSelector(
  characterSpecInfoSelector,
  specInfo => _.get(specInfo, 'id'),
);

const characterSpecSelector = createSelector(
  characterSelector,
  char => _.get(activeLoadout(char), 'talents.specName', ''),
);

const characterFactionSelector = createSelector(
  characterSelector,
  char => getFaction(char),
);

const invalidLoadouts = createSelector(
  characterSelector,
  char => {
    if (char) {
      return _.filter(char.talentLoadouts, loadout => {
        return loadout.talents?.unknownEntries.length > 0;
      });
    }

    // not an issue if no character is present yet
    return [];
  },
);

const hasInvalidTalents = createSelector(
  characterSelector,
  char => {
    if (char) {
      return _.some(char.talentLoadouts, loadout => {
        return loadout.talents?.unknownEntries.length > 0;
      });
    }

    // not an issue if no character is present yet
    return false;
  },
);

const aplSelector = createSelector(
  characterSelector,
  characterClassNameSelector,
  characterSpecSelector,
  customAplsSelectors.customApls,
  (char, className, spec, apls) => _.get(_.find(apls, apl => {
    const aplClassName = _.get(apl, 'className', '').toLowerCase();
    const aplSpec = _.get(apl, 'spec', '').toLowerCase();
    const found = className &&
      spec &&
      className.toLowerCase() === aplClassName &&
      spec.toLowerCase() === aplSpec;
    return found;
  }), 'apl', ''),
);

const aplDataSelector = createSelector(
  aplSelector,
  rawApl => {
    const aplData = SimcInput.parse(rawApl);
    return aplData;
  },
);

const aplInvalidCommandsSelector = createSelector(
  aplDataSelector,
  aplData => {
    const invalidCommands = _.map(SimcInput.getInvalidAplCommands(aplData), item => item.key);
    return invalidCommands;
  },
);

const inputInCustomAplWarningSelector = createSelector(
  aplInvalidCommandsSelector,
  invalidCommands => {
    const hasInputOptions = _.size(
      _.intersection(
        ['level', 'race', 'spec', 'talents'],
        invalidCommands,
      ),
    ) > 0;
    return hasInputOptions;
  },
);

const allRaidBuffsActiveSelector = createSelector(
  simcOptionsSelectors.bloodlust,
  simcOptionsSelectors.arcaneIntellect,
  simcOptionsSelectors.fortitude,
  simcOptionsSelectors.battleShout,
  simcOptionsSelectors.mysticTouch,
  simcOptionsSelectors.chaosBrand,
  simcOptionsSelectors.skyfury,
  simcOptionsSelectors.markOfTheWild,
  simcOptionsSelectors.huntersMark,
  (...buffs) => {
    return _.every(buffs);
  },
);

const simOptionWarningsSelector = createSelector(
  characterSpecInfoSelector,
  simcOptionsSelectors.temporaryEnchant,
  allRaidBuffsActiveSelector,
  simcOptionsSelectors.fightStyle,
  (specInfo, temporaryEnchant, allRaidBuffsActive, fightStyle) => {
    const warnings = [];

    // check for "both hands" temp enchant on invalid spec
    const opt = findExpacOption('temporaryEnchant');
    const value = _.find(opt.values, { value: temporaryEnchant });
    if (specInfo && value?.dualWield) {
      const canDualWield = specInfo.dualWield || specInfo.dualWield2h;
      if (!canDualWield) {
        warnings.push('bad_spec_temporary_enchant_dual_wield');
      }
    }

    // check for dungeon slice and all buffs active
    if (allRaidBuffsActive && fightStyle === 'DungeonSlice') {
      warnings.push('dungeon_slice_all_raid_buffs');
    }

    return warnings;
  },
);

const optimizeGemSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.gem,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);

const optimizeCloakEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.cloakEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeNeckEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.neckEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeRingEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.ringEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeWeaponEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.weaponEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeWeaponOffhandEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.weaponOffhandEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeSelectedEnhancementsSelector = createSelector(
  characterSpecInfoSelector,
  enhancementSelectors.selectedEnhancements,
  (specInfo, selectedEnhancements) => {
    if (specInfo) {
      return _.filter(selectedEnhancements, { specId: specInfo.id });
    }
    return null;
  },
);
const optimizeForceMainStatSelector = createSelector(
  characterSpecInfoSelector,
  enhancementSelectors.forceMainStat,
  (specInfo, forceMainStat) => {
    if (specInfo) {
      return _.get(_.find(forceMainStat, { specId: specInfo.id }), 'value');
    }
    return null;
  },
);
const addSocketGemSelector = createSelector(
  characterSpecInfoSelector,
  enhancementSelectors.addSocketGem,
  (specInfo, addSocketGem) => {
    if (specInfo) {
      const specResult = _.find(addSocketGem, { specId: specInfo.id });
      const gemId = _.get(specResult, 'gemId', '');
      return gemId;
    }
    return null;
  },
);

const selectedGearSelector = createSelector(
  optimizeSelectors.gear,
  optimizeSelectors.selected,
  getSelectedGear,
);
const advancedIterationsSelector = createSelector(
  formSelectors.advancedData,
  simcOptionsSelectors.iterations,
  (data, iterationsOverride) => SimcInput.getTotalIterations(data, { defaultIterations: iterationsOverride }),
);

const enhancementSlotMap = {
  rings: 'finger',
};
const optimizeMissingEnchantsSelector = createSelector(
  // get the versions of this data from optimize, not the more general ones
  optimizeSelectors.equippedGear,
  optimizeSelectors.selectedGear,
  optimizeSelectedEnhancementsSelector,
  (equippedGear, selectedGear, enhancements) => {
    const slotsMissingEnchants = new Set();

    const enchantSlots = CURRENT_SEASON.dpsEnchantSlots;
    _.each(selectedGear, (items, slot) => {
      if (_.includes(enchantSlots, slot)) {
        const mixedEnchants = _.some(items, 'enchant_id') && !_.every(items, 'enchant_id');
        if (mixedEnchants) {
          // check enhancements to see if there is a selection for this slot, ignore zero DPS enchants
          const hasEnchantSelected = _.find(enhancements, enh => {
            return (enh.slot === slot || enh.slot === enhancementSlotMap[slot]) && !enh.zeroDps;
          });
          if (!hasEnchantSelected) {
            // check existing enchants to see if they are zero DPS enchants
            const hasDpsEnchants = _.some(items, item => {
              if (item.enchant_id) {
                const enchant = _.find(getEnchants(), { id: parseInt(item.enchant_id, 10) });
                if (enchant) {
                  return !enchant.zeroDps;
                }
              }
              return false;
            });
            if (hasDpsEnchants) {
              slotsMissingEnchants.add(slot);
            }
          }
        }
      }
    });

    return [...slotsMissingEnchants];
  },
);

// Main check:
//  * Replace existing + unique gem equipped + no unique gem selected
const WARNABLE_UNIQUE_CATEGORIES = [
  501, // Illimited Diamonds (unique primary stat)
];
const optimizeGemWarningsSelector = createSelector(
  optimizeSelectors.equippedGear,
  optimizeSelectors.selectedGear,
  optimizeSelectedEnhancementsSelector,
  optimizeSelectors.enhancementReplace,
  (equippedGear, selectedGear, enhancements, replace) => {
    const warnings = [];

    if (replace) {
      const flatGear = _.flatten(_.values(equippedGear));
      const equippedGems = _.filter(_.flatten(_.map(flatGear, it => it?.socketInfo[SOCKET_TYPES.PRISMATIC]?.gems)));
      const uniqueCategoriesEquipped = _.uniq(_.filter(_.map(equippedGems, 'unique')));
      const warnableCategories = _.filter(uniqueCategoriesEquipped, cat => _.includes(WARNABLE_UNIQUE_CATEGORIES, cat));

      if (warnableCategories.length) {
        // collect categories from selected gems
        const selectedGems = _.map(
          _.filter(enhancements, { slot: 'socket' }),
          enh => getGemDetailsByEnchantId(enh.enhancementId),
        );
        if (selectedGems.length) {
          const selectedCategories = _.uniq(_.filter(_.map(selectedGems, 'unique')));
          const missingCategories = _.without(warnableCategories, ...selectedCategories);
          _.each(missingCategories, catId => {
            warnings.push({
              code: 'missing_unique_category',
              categoryId: catId,
            });
          });

          // also check to make sure at least one normal gem is selected
          if (_.size(_.reject(selectedGems, 'unique')) === 0) {
            warnings.push({
              code: 'missing_normal_gem',
            });
          }
        }
      }
    }

    return warnings;
  },
);

const defaultReportNameSelector = createSelector(
  (state, props) => props.type,
  formSelectors.defaultReportName,
  droptimizerSelectors.reportName,
  (type, defaultReportName, droptimizerReportName) => {
    if (type === 'droptimizer') {
      return droptimizerReportName;
    }
    return defaultReportName;
  },
);

const reportNameSelector = createSelector(
  formSelectors.userReportName,
  defaultReportNameSelector,
  (userReportName, defaultReportName) => {
    return userReportName || defaultReportName;
  },
);

const mainHandAlternatesSelector = createSelector(
  simcItemsSelectors.bags,
  characterClassIdSelector,
  characterSpecIdSelector,
  (bags, classId, specId) => {
    const mainHandWeapons = _.reverse(
      _.clone(_.sortBy(_.filter(bags, { slot: 'main_hand' }), ['itemLevel', 'id'])),
    );
    const oneHanders = _.filter(mainHandWeapons, item => getWeaponHands(item) !== '2h');
    const valid = _.filter(oneHanders, item => item.itemLevel > 10 && isItemUsable(item, classId, specId));
    return valid;
  },
);
const mainHandAlternateWeaponSelector = createSelector(
  mainHandAlternatesSelector,
  droptimizerSelectors.mainHandAlternate,
  (alternates, mainHandAlternateIdx) => {
    // grab first item if the index doesn't exist
    // might happen because i'm changing the behavior because it wasn't properly filtering
    return alternates[mainHandAlternateIdx];
  },
);
const offHandAlternatesSelector = createSelector(
  simcItemsSelectors.bags,
  characterClassIdSelector,
  characterSpecIdSelector,
  (bags, classId, specId) => {
    const offHandWeapons = _.reverse(
      _.clone(_.sortBy(_.filter(bags, { slot: 'off_hand' }), ['itemLevel', 'id'])),
    );
    const valid = _.filter(offHandWeapons, item => item.itemLevel > 10 && isItemUsable(item, classId, specId));
    return valid;
  },
);
const offHandAlternateWeaponSelector = createSelector(
  offHandAlternatesSelector,
  droptimizerSelectors.offHandAlternate,
  (alternates, offHandAlternateIdx) => {
    return alternates[offHandAlternateIdx];
  },
);

const characterLootSpecSelector = createSelector(
  characterClassIdSelector,
  characterSpecIdSelector,
  droptimizerSelectors.lootSpec,
  (classId, specId, lootSpec) => {
    return _.get(lootSpec, `[${classId}][${specId}]`) || specId;
  },
);

const characterCraftedStatsSelector = createSelector(
  characterSpecIdSelector,
  droptimizerSelectors.craftedStats,
  (specId, craftedStats) => craftedStats[specId],
);
const characterCraftedGemSelector = createSelector(
  characterSpecIdSelector,
  droptimizerSelectors.craftedGem,
  (specId, craftedGem) => craftedGem[specId],
);

const droptimizerOptionsSelector = (state, props) => ({
  equipped: equippedGearSelector(state, props),
  mainHandAlternateWeapon: mainHandAlternateWeaponSelector(state, props),
  offHandAlternateWeapon: offHandAlternateWeaponSelector(state, props),
  instance: droptimizerSelectors.selectedInstance(state, props),
  encounter: droptimizerSelectors.selectedEncounter(state, props),
  difficulty: droptimizerSelectors.selectedDifficulty(state, props),
  warforgeLevel: droptimizerSelectors.selectedWarforgeLevel(state, props),
  upgradeLevel: droptimizerSelectors.selectedUpgradeLevel(state, props),
  upgradeEquipped: droptimizerSelectors.upgradeEquipped(state, props),
  gem: droptimizerSelectors.gem(state, props),
  classId: characterClassIdSelector(state, props),
  specId: characterSpecIdSelector(state, props),
  lootSpecId: characterLootSpecSelector(state, props),
  faction: characterFactionSelector(state, props),
  craftedStats: characterCraftedStatsSelector(state, props),
  craftedGem: characterCraftedGemSelector(state, props),
  offSpecItems: droptimizerSelectors.offSpecItems(state, props),
  includeConversions: droptimizerSelectors.includeConversions(state, props),
});

const combinationsSelector = createSelector(
  optimizeSelectors.rawCombinations,
  optimizeSelectors.lockedRing,
  optimizeSelectors.lockedTrinket,
  optimizeSelectors.classId,
  optimizeSelectors.spec,
  enhancementSelectors.selectedEnhancements,
  optimizeSelectors.enhancementReplace,
  enhancementSelectors.forceMainStat,
  optimizeSelectors.equippedGear,
  optimizeSelectors.equippedTalents,
  optimizeSelectors.itemSetRequirements,
  optimizeSelectors.algariColors,
  getCombinations,
);

const numCombinationsSelector = createSelector(
  optimizeSelectors.rawCombinations,
  combinationsSelector,
  simcExpertSelectors.numExpertActors,
  (rawCombos, combosResult, numExpertActors) => {
    let numCombos;
    const combos = combosResult.combinations;
    if (_.isArray(combos)) {
      numCombos = combos.length;
    } else {
      numCombos = rawCombos.combinations.length;
    }

    // base actor + expert/raid actors
    const numActors = 1 + numExpertActors;

    const totalCombos = numCombos * numActors;

    return totalCombos;
  },
);

const droptimizerItemsSelector = (state, props) => {
  if (Droptimizer.ready()) {
    return Droptimizer.getVariations(droptimizerOptionsSelector(state, props));
  }

  return null;
};

const formDataSelector = (state, props) => {
  const data = {
    type: props.type,
    text: formSelectors.text(state, props),
    advancedInput: formSelectors.advancedInput(state, props),

    baseActorName: formSelectors.baseActorName(state, props) || formSelectors.defaultBaseActorName(state, props),
    reportName: reportNameSelector(state, props),

    armory: {
      region: formSelectors.armoryRegion(state, props),
      realm: formSelectors.armoryRealm(state, props),
      name: formSelectors.armoryName(state, props),
    },

    email: formSelectors.email(state, props),
    sendEmail: formSelectors.sendEmail(state, props),

    character: characterSelectors.data(state, props),
    spec: characterSpecSelector(state, props),

    gearsets: gearsetsSelectors.gearsets(state, props),

    talents: characterSelectors.talents(state, props),
    talentSets: talentSetsSelectors.talentSets(state, props),

    optimize: {
      data: optimizeSelectors.data(state, props),
      gear: selectedGearSelector(state, props),
      allGear: optimizeSelectors.gear(state, props),
      numCombinations: numCombinationsSelector(state, props),
      gem: optimizeGemSelector(state, props),
      ringEnchant: optimizeRingEnchantSelector(state, props),
      cloakEnchant: optimizeCloakEnchantSelector(state, props),
      neckEnchant: optimizeNeckEnchantSelector(state, props),
      weaponEnchant: optimizeWeaponEnchantSelector(state, props),
      weaponOffhandEnchant: optimizeWeaponOffhandEnchantSelector(state, props),
      replaceExisting: gemEnchantSelectors.replaceExisting(state, props),
      useMainStatGem: gemEnchantSelectors.useMainStatGem(state, props),
      source: optimizeSelectors.source(state, props),

      lockedRing: optimizeSelectors.lockedRing(state, props),
      lockedTrinket: optimizeSelectors.lockedTrinket(state, props),

      selectedEnhancements: enhancementSelectors.selectedEnhancements(state, props),
      enhancementReplace: optimizeSelectors.enhancementReplace(state, props),
      forceMainStat: enhancementSelectors.forceMainStat(state, props),
      addSocketNum: enhancementSelectors.addSocketNum(state, props),
      addSocketGem: enhancementSelectors.addSocketGem(state, props),

      equippedGear: optimizeSelectors.equippedGear(state, props),
      equippedTalents: optimizeSelectors.equippedTalents(state, props),

      talentLoadouts: talentTreeSelectors.talentLoadouts(state, props),
      level: optimizeSelectors.level(state, props),

      // send these along so they get saved in the report for reloading / run sim again
      selected: optimizeSelectors.selected(state, props),

      upgradeCurrencyConstraints: optimizeSelectors.upgradeCurrencyConstraints(state, props),
      catalystCharges: optimizeSelectors.catalystCharges(state, props),
      itemSetRequirements: optimizeSelectors.itemSetRequirements(state, props),
      algariColors: optimizeSelectors.algariColors(state, props),
    },

    droptimizer: droptimizerOptionsSelector(state, props),
    droptimizerItems: droptimizerItemsSelector(state, props),

    simcVersion: simcOptionsSelectors.simcVersion(state, props),
    iterations: simcOptionsSelectors.iterations(state, props),
    smartHighPrecision: simcOptionsSelectors.smartHighPrecision(state, props),
    smartAggressive: simcOptionsSelectors.smartAggressive(state, props),
    fightStyle: simcOptionsSelectors.fightStyle(state, props),
    fightLength: simcOptionsSelectors.fightLength(state, props),
    enemyCount: simcOptionsSelectors.enemyCount(state, props),
    enemyType: simcOptionsSelectors.enemyType(state, props),
    potion: simcOptionsSelectors.potion(state, props),
    food: simcOptionsSelectors.food(state, props),
    flask: simcOptionsSelectors.flask(state, props),
    augmentation: simcOptionsSelectors.augmentation(state, props),

    bloodlust: simcOptionsSelectors.bloodlust(state, props),
    arcaneIntellect: simcOptionsSelectors.arcaneIntellect(state, props),
    fortitude: simcOptionsSelectors.fortitude(state, props),
    battleShout: simcOptionsSelectors.battleShout(state, props),
    mysticTouch: simcOptionsSelectors.mysticTouch(state, props),
    chaosBrand: simcOptionsSelectors.chaosBrand(state, props),
    bleeding: simcOptionsSelectors.bleeding(state, props),
    skyfury: simcOptionsSelectors.skyfury(state, props),
    markOfTheWild: simcOptionsSelectors.markOfTheWild(state, props),
    powerInfusion: simcOptionsSelectors.powerInfusion(state, props),
    huntersMark: simcOptionsSelectors.huntersMark(state, props),

    reportDetails: simcOptionsSelectors.reportDetails(state, props),

    apl: aplSelector(state, props),

    expertHeader: simcExpertSelectors.headerInput(state, props),
    expertPreActors: simcExpertSelectors.preActorsInput(state, props),
    expertRaidActors: simcExpertSelectors.raidActorsInput(state, props),
    expertPostActors: simcExpertSelectors.postActorsInput(state, props),
    expertFooter: simcExpertSelectors.footerInput(state, props),

    ptr: simcOptionsSelectors.ptr(state, props),

    frontendHost: window.location.host,
    frontendVersion: process.env.VERSION,
  };

  _.each(expansionOptionConfig, option => {
    if (data[option.key]) {
      throw new Error(`formData selector already has property ${option.key} - you screwed up an expansion option`);
    }
    data[option.key] = simcOptionsSelectors[option.key](state, props);
  });

  return data;
};

export const selectors = {
  equippedGear: equippedGearSelector,
  apl: aplSelector,
  aplData: aplDataSelector,
  aplInvalidCommands: aplInvalidCommandsSelector,
  inputInCustomAplWarning: inputInCustomAplWarningSelector,
  character: characterSelector,
  characterClassName: characterClassNameSelector,
  characterSpec: characterSpecSelector,
  characterSpecId: characterSpecIdSelector,
  characterSpecInfo: characterSpecInfoSelector,
  characterFaction: characterFactionSelector,
  invalidLoadouts,
  hasInvalidTalents,
  mainHandAlternates: mainHandAlternatesSelector,
  offHandAlternates: offHandAlternatesSelector,
  mainHandAlternateWeapon: mainHandAlternateWeaponSelector,
  offHandAlternateWeapon: offHandAlternateWeaponSelector,
  characterLootSpec: characterLootSpecSelector,
  characterCraftedStats: characterCraftedStatsSelector,
  characterCraftedGem: characterCraftedGemSelector,
  optimizeGem: optimizeGemSelector,
  optimizeCloakEnchant: optimizeCloakEnchantSelector,
  optimizeNeckEnchant: optimizeNeckEnchantSelector,
  optimizeRingEnchant: optimizeRingEnchantSelector,
  optimizeWeaponEnchant: optimizeWeaponEnchantSelector,
  optimizeWeaponOffhandEnchant: optimizeWeaponOffhandEnchantSelector,
  optimizeSelectedEnhancements: optimizeSelectedEnhancementsSelector,
  optimizeForceMainStat: optimizeForceMainStatSelector,
  optimizeMissingEnchants: optimizeMissingEnchantsSelector,
  optimizeGemWarnings: optimizeGemWarningsSelector,
  addSocketGem: addSocketGemSelector,
  advancedIterations: advancedIterationsSelector,
  formData: formDataSelector,
  reportName: reportNameSelector,
  defaultReportName: defaultReportNameSelector,
  combinations: combinationsSelector,
  numCombinations: numCombinationsSelector,
  simOptionWarnings: simOptionWarningsSelector,
};
